import {
  ApiBaseData,
  ApiBestPricePackageModel,
  ApiGeoUnit,
  ApiGeoUnitFromJSON,
  ApiGeoUnits,
  ApiGetPackagesRequest,
  ApiGetPackagesRequestFromJSON,
  ApiGetPackagesResponse,
  ApiItemType,
  ApiPackageDetails,
  ApiPackageModel,
  ApiRoomContainer,
  ApiRoomContainerFromJSON,
  ApiSortDirection,
  ApiSorting,
  ApiSortOption
} from '@ibe/api';
import { ApiService, ConfigModel, DurationFactory, LoggerFactory, Logger } from '@ibe/services';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import PackageParams from '../../SubModules/PackageDetails/PackageParams';
import Keys from '../../../_Translations/generated/package.en.t.json.keys';
import PackageSearchStore from '../../SubModules/PackageSearch/PackageSearchStore';
import ServiceListStore from '../../SubModules/ServiceList/ServiceListStore';
import ScrollerService from '../../Services/ScrollerService';
import { DestinationItemCategory } from '../../../Models/DestinationItem';

class PackageStore extends ServiceListStore {
  _logger: Logger = LoggerFactory.get('PackageStore');

  searchParams: PackageParams = {};

  _packageListResponse: ApiGetPackagesResponse | null = null;

  packageDetail: ApiPackageModel | ApiBestPricePackageModel | null = null;

  isLoading = false;

  _calculatePrices = true;

  _resultPackageCount = 0;

  error: string | null = null;

  warning: string | null = null;

  listClicked = false;

  geoUnits: ApiGeoUnits = {};

  packageServiceGroups: ApiBaseData[] = [];

  scrollElementGlobalError = 'packageGlobalErrorScroller';

  constructor(
    public api: ApiService,
    public config: ConfigModel,
    initialSorting?: ApiSorting,
    private customSetIsLoading?: (isLoading: boolean) => void
  ) {
    super();

    makeObservable(this, {
      searchParams: observable,
      _packageListResponse: observable,
      packageDetail: observable,
      isLoading: observable,
      error: observable,
      warning: observable,
      geoUnits: observable,
      packageServiceGroups: observable,

      list: computed,
      hasList: computed,
      detail: computed,
      hasDetail: computed,
      getGeoUnit: computed,

      loadPackages: action,
      loadPackage: action,
      setSearchParams: action,
      resetDetail: action,
      setIsLoading: action,
      setCalculatePrices: action
    });

    this._sorting = initialSorting
      ? { ...initialSorting }
      : {
          sortDirection: ApiSortDirection.ASC,
          sortOption: ApiSortOption.PROMOTIONS
        };
  }

  setCalculatePrices(b: boolean) {
    this._calculatePrices = b;
  }

  set getPackagesResponse(res: ApiGetPackagesResponse | null) {
    this._packageListResponse = res;
  }

  get getPackagesResponse(): ApiGetPackagesResponse | null {
    return this._packageListResponse;
  }

  get list(): (ApiPackageModel | ApiBestPricePackageModel)[] {
    return (this._packageListResponse || { packages: [] }).packages;
  }

  get hasList(): boolean {
    return this._packageListResponse != null && this._packageListResponse.listOptions?.count > 0;
  }

  get detail(): ApiPackageModel | ApiBestPricePackageModel | null {
    return this.packageDetail || null;
  }

  get hasDetail(): boolean {
    return !!this.packageDetail;
  }

  get getGeoUnit(): ApiGeoUnit | null {
    const geoUnit = this.searchParams.destination;
    if (geoUnit) {
      return this.geoUnits[geoUnit.id];
    }
    return null;
  }

  resetDetail(): void {
    this.packageDetail = null;
    this.searchParams.packageDetailId = undefined;
    this.searchParams.itemId = undefined;
    this.searchParams.travelDateId = undefined;
  }

  setIsLoading(b: boolean): void {
    if (!!this.customSetIsLoading) {
      this.customSetIsLoading(b);
    } else {
      this.isLoading = b;
    }
  }

  setSearchParams(params: Partial<PackageParams>): void {
    const search = new PackageSearchStore(this.config);
    const { maxInfantAge, treatInfantsAsChildren } = this.config.traveler || {};
    if (params.occupancy && params.occupancy.length !== 0 && !treatInfantsAsChildren) {
      params.occupancy[0].infantsAges =
        !!params?.occupancy[0]?.childrenAges && params.occupancy[0].childrenAges.length > 0
          ? params.occupancy[0].childrenAges.filter(age => age <= maxInfantAge)
          : [];
    }
    search.setParams(params);

    this.searchParams = toJS(search.searchParams);
  }

  applyConfigOnRoomContainers = (occupancy: ApiRoomContainer[]) => {
    const { treatInfantsAsChildren, maxInfantAge } = this.config.traveler;
    return occupancy?.flatMap(roomContainer => {
      const infants = roomContainer.childrenAges
        ? roomContainer.childrenAges.filter((age: number) => age <= maxInfantAge).length
        : 0;
      const childrenAges = roomContainer.childrenAges
        ? roomContainer.childrenAges.filter((age: number) => age > maxInfantAge)
        : [];
      const infantsAges =
        roomContainer.childrenAges && !treatInfantsAsChildren
          ? roomContainer.childrenAges.filter((age: number) => age <= maxInfantAge)
          : [];

      const children = treatInfantsAsChildren
        ? roomContainer.childrenAges.filter((age: number) => age > maxInfantAge).length
        : childrenAges.length;

      const { adults } = roomContainer;

      return ApiRoomContainerFromJSON({ adults, children, infants, childrenAges, infantsAges });
    });
  };

  async loadPackages(subType: ApiItemType, doBestPriceSearch?: boolean): Promise<void> {
    let res: ApiGetPackagesResponse | null = null;
    let error: string | null = null;
    const warning: string | null = null;

    if (this.searchParams.origin && !this.searchParams.origin.id && this.searchParams.origin.code) {
      const response = await this.api.getGeoUnitsForItemType(ApiItemType.FLIGHT);
      const origin: ApiGeoUnit | undefined = Object.values(response.geoUnits).find(
        (geoUnit: ApiGeoUnit) => geoUnit.code === this.searchParams.origin?.code
      );

      if (origin) {
        this.setSearchParams({ ...this.searchParams, origin });
      }
    }

    if (
      this.searchParams.destination &&
      !this.searchParams.destination.id &&
      this.searchParams.destination.code
    ) {
      const response = await this.api.getGeoUnitsForItemType(subType);
      const destination: ApiGeoUnit | undefined = Object.values(response.geoUnits).find(
        (geoUnit: ApiGeoUnit) => geoUnit.code === this.searchParams.destination?.code
      );

      if (origin) {
        this.setSearchParams({ ...this.searchParams, destination });
      }
    }

    if (this.searchParams.destinationItem) {
      const { destinationItem, mainServiceCodes } = this.searchParams;
      if (destinationItem.category === DestinationItemCategory.GEO_UNIT) {
        const dest = ApiGeoUnitFromJSON({
          id: destinationItem.id,
          code: destinationItem.code,
          typeCode: destinationItem.typeCode,
          children: {}
        });
        this.setSearchParams({ ...this.searchParams, destination: dest, productId: undefined });
      } else if (mainServiceCodes && mainServiceCodes.length) {
        this.setSearchParams({
          ...this.searchParams,
          productId: undefined,
          destination: undefined
        });
      } else {
        this.setSearchParams({
          ...this.searchParams,
          productId: destinationItem.id,
          destination: undefined
        });
      }
    }
    const roomContainers = this.applyConfigOnRoomContainers(this.searchParams.occupancy || []);
    const mainServiceCodes = this.searchParams.mainServiceCodes || [];
    const request = ApiGetPackagesRequestFromJSON({
      classCode: '', // what is this?
      destinationAirport: this.searchParams.destination?.id, // TODO clean up model
      destination: this.searchParams.destination?.id,
      destinations: this.searchParams.destinations
        ? this.searchParams.destinations
            .filter(destinationItem => !mainServiceCodes.includes(destinationItem.code))
            .map(destinationItem => destinationItem.id)
        : undefined,
      departureAirport: this.searchParams.origin?.id,
      endDate: this.searchParams.endDate,
      startDate: this.searchParams.startDate,
      origins: this.searchParams.origins
        ? this.searchParams.origins.map(origin => origin.id)
        : undefined,
      roomContainer: roomContainers,
      packageServiceGroup: this.searchParams.packageServiceGroup,
      subType,
      listOptions: {
        filter: this._filter || [],
        sorting: this._sorting || {},
        pagination: this._pagination || {}
      },
      packageCode: this.searchParams.packageCode,
      mainServiceCodes,
      duration: this.searchParams.duration
        ? DurationFactory.create(this.searchParams.duration)
        : undefined,
      salesChannel: this.searchParams.salesChannel,
      productId: this.searchParams.productId,
      preSelectedCharacteristics: this.searchParams?.preSelectedCharacteristics || [],
      inventories: this.searchParams?.inventories || [],
      calculatePrices: this._calculatePrices
    } as ApiGetPackagesRequest);

    try {
      if (doBestPriceSearch) {
        res = await this.api.searchBestPricePackages(request);
      } else {
        res = await this.api.searchPackages(request);
      }
    } catch (e) {
      this._logger.warn(e);
      error = Keys.apiNotAbleToGetPackages;
    }

    runInAction(() => {
      this._packageListResponse = res;
      if (res) {
        this._resultPackageCount = res.packages.length;
        if (res.listOptions) {
          this.setListOptions(res.listOptions);
        }
      }
      this.error = error;
      this.warning = warning;
      if (this._resultPackageCount === 0) {
        this.warning = Keys.emptyResultsWarning;
        if (!!this.listCountFull && this.listCountFull > 0) {
          this.warning = Keys.emptyFilteringResultsWarning;
        }
      }
    });
    this.setSearchParams({ ...this.searchParams, preSelectedCharacteristics: undefined });
  }

  public isPackageModel(
    item: ApiPackageModel | ApiBestPricePackageModel | null
  ): item is ApiPackageModel {
    return (
      (!!(item as ApiPackageModel).packageDetails || !!(item as ApiPackageModel).travelDates) &&
      !(item as ApiBestPricePackageModel).serviceCode
    );
  }

  public isBestPricePackageModel(
    item: ApiPackageModel | ApiBestPricePackageModel | null
  ): item is ApiBestPricePackageModel {
    return !!(item as ApiBestPricePackageModel).serviceCode;
  }

  async loadPackage(): Promise<void> {
    const packageModel = this.list.find(p => p.id === this.searchParams?.itemId);

    if (!packageModel) {
      throw new Error(`PackageModel not found for ID: ${this.searchParams?.itemId}`);
    }

    runInAction(() => {
      this.packageDetail = packageModel || null;

      if (!this.searchParams.packageDetailId && this.isPackageModel(this.packageDetail)) {
        const cheapest = this.packageDetail.packageDetails?.reduce(
          (a: ApiPackageDetails, b: ApiPackageDetails) => {
            if (
              a.price &&
              b.price &&
              Math.min(a.price.finalPrice, b.price.finalPrice) === b.price.finalPrice
            ) {
              return b;
            }
            return a;
          }
        );
        this.setSearchParams({
          ...this.searchParams,
          packageDetailId: cheapest?.id
        });
      } else {
        this.setSearchParams({
          ...this.searchParams
        });
      }
    });
  }

  scrollToGlobalError(): void {
    ScrollerService.scrollTo(this.scrollElementGlobalError);
  }
}

export default PackageStore;
